import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useLocation } from "react-router-dom";
import { fetchAuthSession, signOut } from "../../utils/authSession";
import { useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import { LogOut } from "lucide-react";
import host from '../../global';
import Loader from "../Loader";
import { useTheme } from "../../hooks/useTheme";
import ImpersonationBanner from "../ImpersonationBanner";
import CompleteProfileModal from "./CompleteProfileModal";
import FirstRunGuide from "../firstRunGuide/FirstRunGuide";
import AnalyticsWatcher from "../analytics/AnalyticsWatcher";
import { SourcingConfigProvider } from "../../contexts/SourcingConfigContext";
import PlanStatusBar from "../billing/PlanStatusBar";
import UsageHeadsUp from "../billing/UsageHeadsUp";

// Global error suppression system
let isSessionExpired = false;
let errorSuppressionActive = false;

const Layout = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const orgadmin_accessToken = sessionStorage.getItem("accessToken");
  const impersonationToken = sessionStorage.getItem("impersonationToken");
  const mainRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  const {theme} = useTheme();

  useEffect(() => {
    if (!isAuthenticated || isImpersonating) return;
    if (location.pathname === '/org-admin/editprofile') return;

    const reCheckIfIncomplete = async () => {
      try {
        const token = sessionStorage.getItem('accessToken');
        const res = await fetch(`${host}/getadmindetails`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });
        const data = await res.json();
        if (!data.profile_completed) {
          setShowProfileModal(true);
        } else {
          setShowProfileModal(false);
        }
      } catch (err) {
        console.error('Re-check failed:', err);
      }
    };

    reCheckIfIncomplete();
  }, [location.pathname, isAuthenticated, isImpersonating]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // Global error interceptor - catches ALL errors
  useEffect(() => {
    // Override fetch globally to catch 401s
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (response.status === 401 && !errorSuppressionActive && !isLoggingOutRef.current) {
          errorSuppressionActive = true;
          isSessionExpired = true;
          toast.dismiss();
          setIsAuthenticated(false);
        }
        return response;
      } catch (error) {
        if (!errorSuppressionActive) {
          throw error;
        }
        return Promise.reject(error);
      }
    };

    // Override XMLHttpRequest for any axios/other HTTP libraries
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(...args) {
      this._url = args[1];
      return originalXHROpen.apply(this, args);
    };

    XMLHttpRequest.prototype.send = function(...args) {
      this.addEventListener('readystatechange', function() {
        if (this.readyState === 4 && this.status === 401 && !errorSuppressionActive && !isLoggingOutRef.current) {
          errorSuppressionActive = true;
          isSessionExpired = true;
          toast.dismiss();
          setIsAuthenticated(false);
        }
      });
      return originalXHRSend.apply(this, args);
    };

    // Override console.error to suppress error logs during session expiry
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (!errorSuppressionActive) {
        originalConsoleError(...args);
      }
    };

    // Override toast.error globally
    const originalToastError = toast.error;
    toast.error = (...args) => {
      if (!errorSuppressionActive) {
        originalToastError(...args);
      }
    };

    // Override toast.warn
    const originalToastWarn = toast.warn;
    toast.warn = (...args) => {
      if (!errorSuppressionActive) {
        originalToastWarn(...args);
      }
    };

    // Global error event handler
    const handleGlobalError = (event) => {
      if (errorSuppressionActive) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Global promise rejection handler
    const handleUnhandledRejection = (event) => {
      if (errorSuppressionActive) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleGlobalError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    return () => {
      // Restore original functions
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
      console.error = originalConsoleError;
      toast.error = originalToastError;
      toast.warn = originalToastWarn;
      window.removeEventListener('error', handleGlobalError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);

    // Clear all toasts immediately
    toast.dismiss();

    const checkSession = async () => {
      if (isLoggingOutRef.current) {
        // An explicit logout is in progress -- the Cognito session being gone now
        // is expected, not a timeout. Skip the check instead of flashing "Session Expired".
        setIsLoading(false);
        return;
      }

      try {
        // Validate Cognito session exists first (required for both normal and impersonation flows)
        const session = await fetchAuthSession();
        const { accessToken } = session.tokens ?? {};

        if (!accessToken) {
          throw new Error("No valid session");
        }

        // Check for impersonation (now safe - we know Cognito session is valid)
        if (impersonationToken) {
          const storedUser = localStorage.getItem("impersonatedUser");
          if (storedUser) {
            const impersonatedUser = JSON.parse(storedUser);
            if (impersonatedUser.user_type === "Company_Admin") {
              setIsAuthenticated(true);
              setIsImpersonating(true);
              isSessionExpired = false;
              errorSuppressionActive = false;
              setIsLoading(false);
              return;
            }
          }
        }
        const groups = accessToken.payload["cognito:groups"] ?? [];

        if (
          groups.includes("Company_Admin") &&
          orgadmin_accessToken === accessToken.toString()
        ) {
          setIsAuthenticated(true);
          isSessionExpired = false;
          errorSuppressionActive = false;
        } else {
          await new Promise((resolve) => {
            sessionStorage.removeItem("accessToken");
            resolve();
          });
          await signOut();
          setIsAuthenticated(false);
          isSessionExpired = true;
          errorSuppressionActive = true;
          toast.dismiss();
        }
      } catch (error) {
        setIsAuthenticated(false);
        isSessionExpired = true;
        errorSuppressionActive = true;
        toast.dismiss();
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, [location.pathname, orgadmin_accessToken, impersonationToken]);

  const handleSignOut = async () => {
    isLoggingOutRef.current = true;
    try {
      toast.dismiss();
      errorSuppressionActive = false;
      isSessionExpired = false;
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("impersonationToken");
      localStorage.removeItem("impersonatedUser");
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!isAuthenticated || isSessionExpired) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'app-background ':'bg-bgSecondary'} grad p-4`}>
        <div className="max-w-md w-full">
          <div className="relative overflow-hidden rounded-xl dark:shadow-2xl shadow-sm p-8">
            <div className="absolute inset-0 z-0 dark:glass-effect bg-bgPrimary" />
            <div className="relative z-10 text-center space-y-6">
              <div className="mx-auto dark:bg-white/10 bg-bgSecondary rounded-full p-4 w-fit backdrop-blur-sm">
                <LogOut className="w-8 h-8 dark:text-white text-textPrimary" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold dark:text-white text-textPrimary">
                  Session Expired
                </h2>
                <p className="dark:text-gray-300 text-textSecondary">
                  Please login again to continue your session
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="button"
              >
                <span className="flex gap-2 items-center">
                  <span className="font-bold px-28">Return to Login</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <SourcingConfigProvider>
      <div className={`app-background flex h-screen max-h-screen overflow-hidden ${isImpersonating ? 'pt-10' : ''}`}>
        {isImpersonating && <ImpersonationBanner />}
        {showProfileModal && !isImpersonating && (
          <CompleteProfileModal onClose={() => setShowProfileModal(false)} />
        )}
        {!isImpersonating && <FirstRunGuide suppressed={showProfileModal} />}
        {!isImpersonating && <AnalyticsWatcher />}
        {/* Not while impersonating: opening it marks the heads-up as seen
            for the customer. */}
        {!isImpersonating && <UsageHeadsUp suppressed={showProfileModal} />}
        <Sidebar handleSignOut={handleSignOut} />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header />
          <main
            ref={mainRef}
            className="flex-1 overflow-y-auto p-4 grad"
            style={{
              transition: "margin-left 0.3s ease-in-out",
            }}
          >
            {/* Inside the page, on its background, so it matches both themes. */}
            <PlanStatusBar role="admin" />
            <Outlet />
          </main>
        </div>
      </div>
    </SourcingConfigProvider>
  );

  return (
    <>
      {content}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </>
  );
};

export default Layout;